import {
	DomainName,
	load,
	save,
	SecurityConfigurationSaveModel,
	SecurityConfigurationViewModel
} from "client/Api/SecurityConfiguration/securityConfiguration";
import {
	Form,
	FormItem,
	ToolbarAction,
	ValidationError
} from "client/Components";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { hasMaximumEntries, isNotEmpty } from "client/Validation/rules";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { DomainNameCollection } from "./DomainNameCollection";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const SecurityConfigurationPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [formData, setFormData] = useState<FormData>({
		cspFrameAncestorDomains: []
	});
	const [lastPersistedFormData, setLastPersistedFormData] = useState<FormData>({ ...formData });

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	useActiveSettingsMenuItem(SettingsPageKind.Security);

	useEffect(
		(): (() => void) => {
			const fetch = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const viewModel: SecurityConfigurationSaveModel = await load(signal)
						.catch(handlePageDataLoadingError);

					const formData: FormData = convertToFormData(viewModel);
					setFormData(formData);
					setLastPersistedFormData(formData);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			};

			const cancellation = new AbortController();
			fetch(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager]
	);

	const cancelHandler = useCallback(
		(): void => {
			setFormData(lastPersistedFormData);

			formHandler.validation
				.validate();
		},
		[
			lastPersistedFormData,
			formHandler.validation
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			const { validation } = formHandler;

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);
				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await save(formData, globalCancellation);
				setLastPersistedFormData(formData);
				alertManager.showSuccess(localize("_ChangesSaved"));
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			setIsBusy(false);
		},
		[
			alertManager,
			formData,
			formHandler,
			globalCancellation,
			isBusy
		]
	);

	const defaultActions = useMemo(
		(): readonly SecurityAction[] => {
			return listDefaultActions({
				save: saveHandler,
				cancel: cancelHandler
			});
		},
		[cancelHandler, saveHandler]
	);

	const topBarActions = useMemo(
		(): readonly SecurityAction[] => {
			return buildActionsForTopBar<undefined, SecurityAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const actionContext: SecurityActionContext = undefined;

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={actionContext}
			title={localize("_Security")}
		>
			<SettingsPage.Section title={localize("_EmbedWorkspace")}>
				<Form
					onSubmit={saveHandler}
					hasWidthConstrained
				>
					<FormItem
						label={{
							template: localize("_ListDomainsThatAreAllowedToEmbedTheWorkspaceMaxAmount"),
							replacements: [
								{ what: "{{Amount}}", filler: MAX_CSP_DOMAINS.toString() }
							]
						}}
						isForGroup
					>
						<DomainNameCollection
							attachable={formHandler.for("cspFrameAncestorDomains")}
							maxAmount={MAX_CSP_DOMAINS}
						/>

						<ValidationError {...formHandler.for("cspFrameAncestorDomains").attachToValidationError(true)} />
					</FormItem>
				</Form>
			</SettingsPage.Section>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
SecurityConfigurationPage.displayName = "SecurityConfigurationPage";

type FormData = Omit<SecurityConfigurationSaveModel, "cspFrameAncestorDomains"> & {
	cspFrameAncestorDomains: readonly ObjectWithClientId<DomainName>[];
};

const MAX_CSP_DOMAINS = 10;

const validationConfiguration: IValidationConfiguration<FormData> = createValidationConfiguration();

const cspDomainsValidator = validationConfiguration.forCollection("cspFrameAncestorDomains");

cspDomainsValidator.add(
	hasMaximumEntries(MAX_CSP_DOMAINS)
);

cspDomainsValidator.forItems((x) => getClientId(x))
	.forScalar("value")
	.add(
		isNotEmpty()
	);

function convertToFormData(viewModel: SecurityConfigurationViewModel): FormData {
	return {
		cspFrameAncestorDomains: viewModel.cspFrameAncestorDomains
			.map(setClientId)
	};
}

type SecurityActionContext = undefined;
type SecurityAction = ToolbarAction<SecurityActionContext>;

type DefaultActionsHandlers = {
	save: () => void;
	cancel: () => void;
};

function listDefaultActions(handlers: DefaultActionsHandlers): readonly SecurityAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			icon: "floppy-disk",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => handlers.save(),
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			icon: "xmark",
			title: localize("_Cancel"),
			canExecute: () => true,
			execute: () => handlers.cancel()
		}
	];
}

export const securityConfigurationRouting = {
	path: "/SecurityConfiguration"
};
